import { useState, useRef, useCallback, useEffect } from 'react'
import { Bold, Italic, Underline, Link, AlignLeft, AlignCenter, AlignRight, AlignJustify, List, ListOrdered, Strikethrough, RemoveFormatting } from 'lucide-react'
import MergeTagDropdown from './MergeTagDropdown'

const TOOLBAR_BUTTONS = [
  { cmd: 'bold', icon: Bold, title: 'Bold (Ctrl+B)' },
  { cmd: 'italic', icon: Italic, title: 'Italic (Ctrl+I)' },
  { cmd: 'underline', icon: Underline, title: 'Underline (Ctrl+U)' },
  { cmd: 'strikeThrough', icon: Strikethrough, title: 'Strikethrough' },
  { type: 'divider' },
  { cmd: 'justifyLeft', icon: AlignLeft, title: 'Align Left' },
  { cmd: 'justifyCenter', icon: AlignCenter, title: 'Align Center' },
  { cmd: 'justifyRight', icon: AlignRight, title: 'Align Right' },
  { cmd: 'justifyFull', icon: AlignJustify, title: 'Justify' },
  { type: 'divider' },
  { cmd: 'insertUnorderedList', icon: List, title: 'Bullet List' },
  { cmd: 'insertOrderedList', icon: ListOrdered, title: 'Numbered List' },
  { type: 'divider' },
  { cmd: 'createLink', icon: Link, title: 'Insert Link' },
  { cmd: 'removeFormat', icon: RemoveFormatting, title: 'Clear Formatting' },
]

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || ''
    }
  }, [])

  const execCmd = useCallback((cmd) => {
    if (cmd === 'createLink') {
      const url = prompt('Enter URL:')
      if (url) {
        document.execCommand('createLink', false, url)
      }
      return
    }
    document.execCommand(cmd, false, null)
    editorRef.current?.focus()
  }, [])

  const handleInput = useCallback(() => {
    const html = editorRef.current?.innerHTML || ''
    onChange(html)
  }, [onChange])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;')
    }
  }, [])

  const isActive = (cmd) => {
    try {
      return document.queryCommandState(cmd)
    } catch {
      return false
    }
  }

  return (
    <div style={{
      border: `1px solid ${isFocused ? 'var(--color-primary)' : '#e2e8f0'}`,
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'border-color 0.2s',
      background: '#fff'
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1px',
        padding: '4px 6px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        alignItems: 'center'
      }}>
        {TOOLBAR_BUTTONS.map((btn, idx) => {
          if (btn.type === 'divider') {
            return (
              <div key={`div-${idx}`} style={{
                width: '1px',
                height: '18px',
                backgroundColor: '#e2e8f0',
                margin: '0 2px'
              }} />
            )
          }

          const Icon = btn.icon
          return (
            <button
              key={btn.cmd}
              onMouseDown={(e) => {
                e.preventDefault()
                execCmd(btn.cmd)
              }}
              title={btn.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '26px',
                height: '26px',
                border: 'none',
                borderRadius: '4px',
                background: 'transparent',
                color: '#475569',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#e2e8f0'
                e.currentTarget.style.color = 'var(--color-primary)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = '#475569'
              }}
            >
              <Icon size={14} />
            </button>
          )
        })}
        <div style={{ width: '1px', height: '18px', backgroundColor: '#e2e8f0', margin: '0 2px' }} />
        <MergeTagDropdown onInsert={(tag) => {
          editorRef.current?.focus()
          document.execCommand('insertHTML', false, `<span style="color:#6366f1;font-weight:600;">${tag}</span>&nbsp;`)
          handleInput()
        }} />
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder || 'Type your content here...'}
        style={{
          minHeight: '120px',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '10px 12px',
          fontSize: '0.85rem',
          lineHeight: '1.6',
          color: '#1e293b',
          outline: 'none',
          fontFamily: 'inherit',
          wordBreak: 'break-word'
        }}
      />

      <style>{`
        [contentEditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
      `}</style>
    </div>
  )
}
